import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, FileText, CheckCircle, AlertCircle, Flag, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { formatDate, NOTE_TYPE_CONFIG } from '@/lib/utils';
import type { PropertyNote, NoteType } from '@/types';

const ICONS = {
  note: FileText,
  decision: CheckCircle,
  problem: AlertCircle,
  milestone: Flag,
};

interface PropertyTimelineProps {
  propertyId: string;
}

export function PropertyTimeline({ propertyId }: PropertyTimelineProps) {
  const [notes, setNotes] = useState<PropertyNote[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newNote, setNewNote] = useState({ type: 'note' as NoteType, content: '' });

  async function fetchNotes() {
    const { data } = await supabase
      .from('property_notes')
      .select('*, author:profiles(*)')
      .eq('property_id', propertyId)
      .order('created_at', { ascending: false });
    setNotes(data || []);
  }

  useEffect(() => {
    fetchNotes();
  }, [propertyId]);

  const handleAdd = async () => {
    if (!newNote.content.trim()) return;

    const { data: { user } } = await supabase.auth.getUser();

    await supabase.from('property_notes').insert({
      property_id: propertyId,
      note_type: newNote.type,
      content: newNote.content,
      created_by: user?.id,
    });

    setNewNote({ type: 'note', content: '' });
    setIsAdding(false);
    fetchNotes();
  };

  const handleDelete = async (id: string) => {
    await supabase.from('property_notes').delete().eq('id', id);
    fetchNotes();
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-heading">Timeline</h3>
        <Button variant="secondary" size="sm" onClick={() => setIsAdding(true)}>
          <Plus className="w-4 h-4 mr-2" /> Eintrag
        </Button>
      </div>

      {/* Add Form */}
      {isAdding && (
        <Card className="p-4 space-y-3">
          <div className="flex gap-2 flex-wrap">
            {(Object.keys(NOTE_TYPE_CONFIG) as NoteType[]).map(type => {
              const config = NOTE_TYPE_CONFIG[type];
              const Icon = ICONS[type];
              return (
                <button
                  key={type}
                  onClick={() => setNewNote(n => ({ ...n, type }))}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors ${
                    newNote.type === type
                      ? 'bg-background-elevated ' + config.color
                      : 'text-foreground-muted hover:text-foreground hover:bg-background-subtle'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {config.label}
                </button>
              );
            })}
          </div>
          <textarea
            value={newNote.content}
            onChange={(e) => setNewNote(n => ({ ...n, content: e.target.value }))}
            placeholder="Was ist passiert?"
            className="input w-full h-24 resize-none bg-background border-border rounded-md p-3"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setIsAdding(false)}>
              Abbrechen
            </Button>
            <Button size="sm" onClick={handleAdd}>
              Speichern
            </Button>
          </div>
        </Card>
      )}

      {/* Timeline */}
      <div className="relative">
        {/* Vertical Line */}
        {notes.length > 0 && (
          <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
        )}

        <div className="space-y-6">
          {notes.map((note, i) => {
            const config = NOTE_TYPE_CONFIG[note.note_type];
            const Icon = ICONS[note.note_type];

            return (
              <motion.div
                key={note.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="relative pl-10 group"
              >
                {/* Icon */}
                <div className={`absolute left-0 w-8 h-8 rounded-full bg-background-subtle border border-border flex items-center justify-center z-10 ${config.color}`}>
                  <Icon className="w-4 h-4" />
                </div>

                {/* Content */}
                <Card className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className={`text-xs font-medium ${config.color}`}>
                        {config.label}
                      </span>
                      <span className="text-xs text-foreground-muted ml-2">
                        {formatDate(note.created_at)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDelete(note.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-background-elevated rounded transition-opacity"
                    >
                      <Trash2 className="w-4 h-4 text-foreground-muted hover:text-negative" />
                    </button>
                  </div>
                  <p className="mt-2 text-sm whitespace-pre-wrap">{note.content}</p>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {notes.length === 0 && !isAdding && (
          <Card className="p-8 text-center ml-10">
            <p className="text-foreground-muted">Noch keine Einträge. Dokumentiere wichtige Ereignisse!</p>
          </Card>
        )}
      </div>
    </div>
  );
}
